import type { CSSProperties, ReactElement } from "react";
import { ArrowLeft, SlidersHorizontal } from "lucide-react";
import { color, space } from "@tokens/index";
import { Badge, Button, IconButton, Text } from "@primitives/index";

export type FiltersControlMode = "collapsible" | "sheet";

export interface FiltersControl {
  readonly mode: FiltersControlMode;
  readonly open: boolean;
  readonly count: number;
  readonly onToggle: () => void;
  /** Id of the rail the button expands in `collapsible` mode. */
  readonly controlsId: string;
}

export interface TimelineHeaderProps {
  readonly onBack?: (() => void) | undefined;
  readonly subjectTitle?: string | undefined;
  readonly viewSwitcher?: ReactElement | undefined;
  readonly filtersControl?: FiltersControl | undefined;
  /** Put the controls on a second row under the title. */
  readonly stacked: boolean;
}

const headerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: space[2],
  paddingInline: space[4],
  paddingTop: space[3],
  paddingBottom: space[2],
  flexShrink: 0,
  minWidth: 0,
  borderBottom: `1px solid ${color.border.subtle}`,
};

const titleRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[3],
  minWidth: 0,
};

const titleBlockStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  flex: 1,
};

const controlsStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: space[2],
  flexShrink: 0,
  marginLeft: "auto",
};

const controlsRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: space[2],
  minWidth: 0,
};

const trailingStyle: CSSProperties = {
  marginLeft: "auto",
  flexShrink: 0,
};

export function FiltersButton({ control }: { readonly control: FiltersControl }): ReactElement {
  const popup = control.mode === "sheet";
  return (
    <Button
      variant="secondary"
      size="md"
      onClick={control.onToggle}
      aria-expanded={control.open}
      aria-haspopup={popup ? "dialog" : undefined}
      aria-controls={popup ? undefined : control.controlsId}
    >
      <SlidersHorizontal size={16} strokeWidth={2.25} aria-hidden="true" />
      Filters
      {control.count > 0 ? (
        <Badge variant="accent" caps={false}>
          {control.count}
        </Badge>
      ) : null}
    </Button>
  );
}

/**
 * Timeline header. One row on `medium` and wider: back, title, controls.
 * On `compact` the controls move to a second row so the bar never
 * overflows.
 */
export function TimelineHeader({
  onBack,
  subjectTitle,
  viewSwitcher,
  filtersControl,
  stacked,
}: TimelineHeaderProps): ReactElement {
  const hasControls = viewSwitcher !== undefined || filtersControl !== undefined;
  const controls = hasControls ? (
    <>
      {filtersControl !== undefined ? <FiltersButton control={filtersControl} /> : null}
      {viewSwitcher !== undefined ? <div style={trailingStyle}>{viewSwitcher}</div> : null}
    </>
  ) : null;
  const twoRows = stacked && hasControls;

  return (
    <header style={headerStyle}>
      <div style={titleRowStyle}>
        {onBack !== undefined ? (
          <IconButton
            icon={ArrowLeft}
            aria-label="Back to workspace"
            tooltip="Back"
            variant="ghost"
            size="md"
            onClick={onBack}
          />
        ) : null}
        <div style={titleBlockStyle}>
          <Text role="eyebrow" tone="subtle" as="p">
            Audit timeline
          </Text>
          {subjectTitle !== undefined && subjectTitle.length > 0 ? (
            <Text role="title" as="h1" truncate>
              {subjectTitle}
            </Text>
          ) : null}
        </div>
        {twoRows ? null : controls !== null ? <div style={controlsStyle}>{controls}</div> : null}
      </div>
      {twoRows ? <div style={controlsRowStyle}>{controls}</div> : null}
    </header>
  );
}
